import { useEffect, useRef, useState } from 'react';
import { useToggle } from '@/hooks/useToggle';
import { showErrorToaster } from '@/utils/toasters';
import Slider from '@/components/Slider';
import PlayButton from './PlayButton';
import PlaybackSpeedButton, { type Speed } from './PlaybackSpeedButton';
import { classes } from './utils';

export type SequenceSliderProps = {
  position: number;
  stepsCount: number;
  onSelect: (position: number) => void;
};

export default function SequenceSlider({
  position,
  stepsCount,
  onSelect,
}: SequenceSliderProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const [speed, setSpeed] = useState<Speed>(1);
  const [isPlaying, togglePlaying] = useToggle();

  useEffect(() => {
    window.addEventListener('keyup', handleKeyPress);
    return () => {
      window.removeEventListener('keyup', handleKeyPress);
    };
  }, [position]);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => handleIncrement(1), 800 / speed);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, speed, position]);

  const handleKeyPress = (e: KeyboardEvent) => {
    if (
      e.target &&
      ['HTMLInputElement', 'HTMLTextAreaElement'].includes(
        e.target.constructor.name
      )
    ) {
      return;
    }
    if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      handleIncrement(e.key === 'ArrowRight' ? 1 : -1);
    } else if (e.code === 'Space') {
      e.preventDefault();
      e.stopPropagation();
      togglePlaying();
    }
  };

  const handleIncrement = (inc: 1 | -1) => {
    let newPosition = position + inc;
    if (newPosition === -1) {
      // Loop back to the end
      newPosition = stepsCount - 1;
    } else if (newPosition === stepsCount - 1) {
      // Loop back to the start
      newPosition = 0;
    }
    onSelect(newPosition);
  };

  const handleChange = (pos: number) => {
    if (pos < 0 || pos > stepsCount) {
      showErrorToaster('Step not found');
      throw Error('Step not found');
    }
    return onSelect(pos);
  };

  if (stepsCount === 0) return null;

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.playWrapper}>
          <PlayButton isPlaying={isPlaying} onChange={() => togglePlaying()} />
          <PlaybackSpeedButton speed={speed} onChange={(s) => setSpeed(s)} />
        </div>
        <Slider
          marks
          max={stepsCount - 1}
          value={position}
          onChange={handleChange}
        />
      </div>
    </>
  );
}
